import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Text,
  View,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import AssignJobListView from "./AssignJobListComponent";
import JobCardView from "./JobCardComponent";
import { FONT_FAMILY, THEME_COLORS } from "../../../common/themes";
import Modal from "modal-enhanced-react-native-web";
import { Ionicons as Icon } from "@expo/vector-icons";
import { useLazyQuery } from "@apollo/client";
import { clientV2 } from "../../../common";
import { TooltipV3 } from "../../../common/component";
import { GET_JOB_LISTS_FOR_REFURB_CAR } from "../query";

const CustomTab = ({
  title,
  id,
  createdOn,
  journey,
  car,
  cgt,
  workshop,
  isSelected = true,
  onPress,
}) => {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={{ marginHorizontal: 10, opacity: isSelected ? 1 : 0.45 }}
    >
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <Text style={{ fontFamily: FONT_FAMILY.three, fontSize: 16 }}>
          {title} ID {id}
        </Text>
        <View
          style={{
            paddingHorizontal: 7,
            paddingVertical: 2,
            marginLeft: 5,
            backgroundColor: "#E6E6E6",
            borderRadius: 6,
          }}
        >
          <Text
            style={{
              fontFamily: FONT_FAMILY.two,
              color: "black",
              fontSize: 12,
            }}
          >
            Created on {createdOn}
          </Text>
        </View>
      </View>
      <Text
        style={{ fontFamily: FONT_FAMILY.three, color: "black", fontSize: 14 }}
      >
        {journey} • {car}
      </Text>
      {cgt ? (
        <Text
          style={{ fontFamily: FONT_FAMILY.two, color: "grey", fontSize: 14 }}
        >
          CGT: {cgt}
        </Text>
      ) : null}
      {workshop ? (
        <TooltipV3 title={workshop}>
          <Text
            style={{
              maxWidth: 250,
              fontFamily: FONT_FAMILY.two,
              color: "grey",
              fontSize: 14,
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 1,
              wordBreak: "break-word",
            }}
          >
            {workshop}
          </Text>
        </TooltipV3>
      ) : null}
      <View
        style={{
          height: 3,
          backgroundColor: isSelected ? THEME_COLORS.primaryPurple : "white",
          width: 35,
          borderRadius: 8,
          marginTop: 5,
        }}
      />
    </TouchableOpacity>
  );
};

const JobListsForRefubCar = ({ refurbCarId, onClose }) => {
  const [getJoblistForRefurbCar, { data, loading, error }] = useLazyQuery(
    GET_JOB_LISTS_FOR_REFURB_CAR,
    {
      variables: { refurbCarId: "" },
      client: clientV2,
      fetchPolicy: "network-only",
    }
  );

  const [selectedJobCardId, setSelectedJobCardId] = useState(null);
  const [selectedJobListIndex, setSelectedJobListIndex] = useState(0);

  useEffect(() => {
    if (!!refurbCarId) {
      const id: string = refurbCarId?.toString();
      getJoblistForRefurbCar({
        variables: {
          refurbCarId: id,
        },
      });
    } else {
      setSelectedJobListIndex(0);
      setSelectedJobCardId(null);
    }
  }, [refurbCarId]);

  const renderTabView = useCallback(
    ({ item: jobList, index }) => {
      const { workshopRefurbCars, id } = jobList;
      const car = jobList?.car?.car;
      const carDetails = `${car?.model} ${car?.year?.replace("A_", "")} • ${
        car?.registrationNo
      }`;
      const journey = jobList?.journey?.displayName;

      return (
        <>
          <CustomTab
            title={"JobList"}
            id={id}
            createdOn={jobList?.createdAt?.split("T")?.[0]?.replace("-", "/")}
            journey={journey}
            car={carDetails}
            cgt={workshopRefurbCars
              ?.map((obj) => obj?.assignedCgt?.fullName)
              ?.join(", ")}
            workshop={workshopRefurbCars
              ?.map((obj) => obj.workshop.displayName || "N/A")
              ?.join(", ")}
            isSelected={!!!selectedJobCardId && selectedJobListIndex == index}
            onPress={() => {
              setSelectedJobListIndex(index);
              setSelectedJobCardId(null);
            }}
          />
          {workshopRefurbCars?.map((obj, index) => {
            return (
              <CustomTab
                title={"JobCard"}
                key={index}
                id={obj?.id}
                createdOn={obj?.createdAt?.split("T")?.[0]?.replace("-", "/")}
                journey={journey}
                car={carDetails}
                cgt={obj?.assignedCgt?.fullName}
                workshop={obj.workshop.displayName || "N/A"}
                isSelected={obj.id === selectedJobCardId}
                onPress={() => {
                  setSelectedJobCardId(obj.id);
                  setSelectedJobListIndex(null);
                }}
              />
            );
          })}
        </>
      );
    },
    [selectedJobCardId, selectedJobListIndex]
  );

  return (
    <Modal
      style={{ margin: 20 }}
      isVisible={refurbCarId}
      onBackdropPress={onClose}
    >
      <View
        style={[
          {
            backgroundColor: "white",
            flex: 1,
            padding: 15,
            borderRadius: 10,
          },
        ]}
      >
        {loading ? (
          <View
            style={{ alignItems: "center", justifyContent: "center", flex: 1 }}
          >
            <ActivityIndicator color={THEME_COLORS.purple} size={"large"} />
            <Text style={styles.loading}>Loading...</Text>
          </View>
        ) : (
          <>
            <View
              style={[
                {
                  width: "100%",
                  flexDirection: "row",
                  backgroundColor: "white",
                  alignItems: "center",
                  marginBottom: 30,
                },
              ]}
            >
              <FlatList
                showsHorizontalScrollIndicator={false}
                horizontal
                data={data?.getJobListsForRefurbCar || []}
                keyExtractor={(_, i) => _?.id?.toString()}
                renderItem={renderTabView}
                extraData={[selectedJobCardId, selectedJobListIndex]}
              />
              <View style={{ flex: 1 }} />
              <TouchableOpacity
                onPress={onClose}
                style={{ alignSelf: "flex-start" }}
              >
                <View
                  style={{
                    borderWidth: 2,
                    borderColor: "rgb(240,240,240)",
                    paddingHorizontal: 5,
                    paddingVertical: 2,
                    borderRadius: 5,
                  }}
                >
                  <Icon name="close" style={{ fontSize: 20 }} />
                </View>
              </TouchableOpacity>
            </View>
            <View style={{ flex: 1, paddingHorizontal: 10 }}>
              {data?.getJobListsForRefurbCar?.length ? (
                selectedJobCardId ? (
                  <JobCardView jobCardId={selectedJobCardId} />
                ) : (
                  <AssignJobListView
                    jobList={
                      data?.getJobListsForRefurbCar?.[selectedJobListIndex] ||
                      {}
                    }
                  />
                )
              ) : (
                <Text>No Data</Text>
              )}
            </View>
          </>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  loading: {
    fontSize: 18,
    fontFamily: FONT_FAMILY.primary,
    fontWeight: "500",
    textAlign: "center",
    marginTop: 10,
  },
});
export default memo(JobListsForRefubCar);
